"use client"

import { createContext, useContext, useEffect, useState } from "react"
import { useTheme } from "next-themes"

interface ThemeColors {
  primary: string
  primaryForeground: string
  secondary: string
  secondaryForeground: string
  accent: string
  accentForeground: string
  background: string
  foreground: string
  card: string
  cardForeground: string
  popover: string
  popoverForeground: string
  muted: string
  mutedForeground: string
  border: string
  input: string
  ring: string
  destructive: string
  destructiveForeground: string
}

interface ThemeGradients {
  primary: { light: string; dark: string }
  secondary: { light: string; dark: string }
  accent: { light: string; dark: string }
  background: { light: string; dark: string }
  hero: { light: string; dark: string }
}

interface ThemeShadows {
  glow: { light: string; dark: string }
  card: { light: string; dark: string }
}

interface ThemeSemantic {
  success: { light: string; dark: string }
  warning: { light: string; dark: string }
  error: { light: string; dark: string }
  info: { light: string; dark: string }
}

interface ThemeConfig {
  name: string
  description: string
  colors: {
    light: ThemeColors
    dark: ThemeColors
  }
  gradients: ThemeGradients
  shadows: ThemeShadows
  semantic: ThemeSemantic
}

interface ThemeSystemContextType {
  theme: ThemeConfig | null
  currentMode: 'light' | 'dark'
  currentTheme: string
  availableThemes: string[]
  getColor: (colorKey: keyof ThemeColors) => string
  getGradient: (gradientKey: keyof ThemeGradients) => string
  getShadow: (shadowKey: keyof ThemeShadows) => string
  getSemantic: (semanticKey: keyof ThemeSemantic) => string
  loadTheme: (themeName: string) => Promise<void>
  setCurrentTheme: (themeName: string) => void
}

const ThemeSystemContext = createContext<ThemeSystemContextType | undefined>(undefined)

// Helper function to convert Tailwind gradient classes to CSS gradient
function convertTailwindGradient(tailwindGradient: string): string {
  // Handle format like "from-blue-600 via-blue-500 to-indigo-500"
  const parts = tailwindGradient.split(' ')
  let fromColor = ''
  let viaColor = ''
  let toColor = ''
  
  parts.forEach(part => {
    if (part.startsWith('from-')) {
      fromColor = convertTailwindColor(part.replace('from-', ''))
    } else if (part.startsWith('via-')) {
      viaColor = convertTailwindColor(part.replace('via-', ''))
    } else if (part.startsWith('to-')) {
      toColor = convertTailwindColor(part.replace('to-', ''))
    }
  })
  
  if (viaColor) {
    return `linear-gradient(135deg, ${fromColor} 0%, ${viaColor} 50%, ${toColor} 100%)`
  } else {
    return `linear-gradient(135deg, ${fromColor} 0%, ${toColor} 100%)`
  }
}

// Helper function to convert Tailwind color classes to CSS color values
function convertTailwindColor(colorClass: string): string {
  const colorMap: Record<string, Record<string, string>> = {
    'blue': {
      '50': 'rgb(239 246 255)',
      '100': 'rgb(219 234 254)',
      '200': 'rgb(191 219 254)',
      '300': 'rgb(147 197 253)',
      '400': 'rgb(96 165 250)',
      '500': 'rgb(59 130 246)',
      '600': 'rgb(37 99 235)',
      '700': 'rgb(29 78 216)',
      '800': 'rgb(30 64 175)',
      '900': 'rgb(30 58 138)',
      '950': 'rgb(23 37 84)'
    },
    'indigo': {
      '50': 'rgb(238 242 255)',
      '100': 'rgb(224 231 255)',
      '200': 'rgb(199 210 254)',
      '300': 'rgb(165 180 252)',
      '400': 'rgb(129 140 248)',
      '500': 'rgb(99 102 241)',
      '600': 'rgb(79 70 229)',
      '700': 'rgb(67 56 202)',
      '800': 'rgb(55 48 163)',
      '900': 'rgb(49 46 129)',
      '950': 'rgb(30 27 75)'
    },
    'purple': {
      '50': 'rgb(250 245 255)',
      '100': 'rgb(243 232 255)',
      '200': 'rgb(233 213 255)',
      '300': 'rgb(196 181 253)',
      '400': 'rgb(168 162 248)',
      '500': 'rgb(139 92 246)',
      '600': 'rgb(124 58 237)',
      '700': 'rgb(109 40 217)',
      '800': 'rgb(91 33 182)',
      '900': 'rgb(76 29 149)',
      '950': 'rgb(46 16 101)'
    },
    'cyan': {
      '50': 'rgb(236 254 255)',
      '100': 'rgb(207 250 254)',
      '200': 'rgb(165 243 252)',
      '300': 'rgb(103 232 249)',
      '400': 'rgb(34 211 238)',
      '500': 'rgb(6 182 212)',
      '600': 'rgb(8 145 178)',
      '700': 'rgb(14 116 144)',
      '800': 'rgb(21 94 117)',
      '900': 'rgb(22 78 99)',
      '950': 'rgb(8 51 68)'
    },
    'slate': {
      '50': 'rgb(248 250 252)',
      '100': 'rgb(241 245 249)',
      '200': 'rgb(226 232 240)',
      '300': 'rgb(203 213 225)',
      '400': 'rgb(148 163 184)',
      '500': 'rgb(100 116 139)',
      '600': 'rgb(71 85 105)',
      '700': 'rgb(51 65 85)',
      '800': 'rgb(30 41 59)',
      '900': 'rgb(15 23 42)',
      '950': 'rgb(2 6 23)'
    },
    'gray': {
      '50': 'rgb(249 250 251)',
      '100': 'rgb(243 244 246)',
      '200': 'rgb(229 231 235)',
      '300': 'rgb(209 213 219)',
      '400': 'rgb(156 163 175)',
      '500': 'rgb(107 114 128)',
      '600': 'rgb(75 85 99)',
      '700': 'rgb(55 65 81)',
      '800': 'rgb(31 41 55)',
      '900': 'rgb(17 24 39)',
      '950': 'rgb(3 7 18)'
    }
  }
  
  // Handle colors with opacity like "blue-50/20"
  const [colorPart, opacity] = colorClass.split('/')
  const [colorName, shade] = colorPart.split('-')
  
  const color = colorMap[colorName]?.[shade] || `rgb(99 102 241)` // fallback to indigo-500
  
  if (opacity) {
    // Convert opacity percentage to decimal
    const opacityValue = parseInt(opacity) / 100
    return color.replace('rgb(', `rgb(`).replace(')', ` / ${opacityValue})`)
  }
  
  return color
}

export function useThemeSystem() {
  const context = useContext(ThemeSystemContext)
  if (context === undefined) {
    throw new Error('useThemeSystem must be used within a ThemeSystemProvider')
  }
  return context
}

export function ThemeSystemProvider({ children }: { children: React.ReactNode }) {
  const [themeConfig, setThemeConfig] = useState<ThemeConfig | null>(null)
  const [currentTheme, setCurrentThemeState] = useState<string>('default')
  const [availableThemes] = useState<string[]>(['default', 'ocean', 'forest', 'sunset', 'midnight', 'sakura', 'aurora', 'ember', 'cosmic', 'monochrome'])
  const { theme: nextTheme, resolvedTheme } = useTheme()
  const currentMode = (resolvedTheme === 'dark' ? 'dark' : 'light') as 'light' | 'dark'

  const loadTheme = async (themeName: string = 'default') => {
    try {
      console.log(`Loading theme: ${themeName}`)
      const response = await fetch(`/themes/${themeName}.json`)
      if (!response.ok) {
        throw new Error(`Failed to load theme: ${themeName}`)
      }
      const theme = await response.json()
      console.log('Theme loaded:', theme)
      setThemeConfig(theme)
      applyThemeToCSS(theme, currentMode)
    } catch (error) {
      console.error('Error loading theme:', error)
      // Fallback to default theme if loading fails
      if (themeName !== 'default') {
        await loadTheme('default')
      }
    }
  }

  const applyThemeToCSS = (theme: ThemeConfig, mode: 'light' | 'dark') => {
    if (!theme) return
    
    console.log(`Applying theme to CSS - mode: ${mode}`, theme)
    const root = document.documentElement
    const colors = theme.colors[mode]

    // Apply CSS variables with !important to override defaults
    Object.entries(colors).forEach(([key, value]) => {
      const cssVar = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`
      // console.log(`Setting CSS variable: ${cssVar} = ${value}`)
      root.style.setProperty(cssVar, value, 'important')
    })

    // Apply semantic colors
    Object.entries(theme.semantic).forEach(([key, value]) => {
      const cssVar = `--semantic-${key}`
      root.style.setProperty(cssVar, value[mode])
    })

    // Apply gradients to CSS variables
    Object.entries(theme.gradients).forEach(([key, value]) => {
      const cssVar = `--gradient-${key}`
      const gradientValue = convertTailwindGradient(value[mode])
      root.style.setProperty(cssVar, gradientValue)
    })

    // Apply shadows
    Object.entries(theme.shadows).forEach(([key, value]) => {
      const cssVar = `--shadow-${key}`
      root.style.setProperty(cssVar, value[mode])
    })

    // Update existing style element or create new one
    let styleElement = document.getElementById('theme-system-styles')
    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = 'theme-system-styles'
      document.head.appendChild(styleElement)
    }

    styleElement.textContent = `
      :root {
        --radius: 0.75rem;
      }
      
      .theme-gradient-primary {
        background: var(--gradient-primary);
      }
      
      .theme-gradient-secondary {
        background: var(--gradient-secondary);
      }
      
      .theme-gradient-accent {
        background: var(--gradient-accent);
      }
      
      .theme-gradient-background {
        background: var(--gradient-background);
      }
      
      .theme-gradient-hero {
        background: var(--gradient-hero);
      }
      
      .theme-gradient-text {
        background: linear-gradient(135deg, rgb(var(--primary)) 0%, rgb(var(--accent)) 50%, rgb(var(--primary)) 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        background-size: 200% 100%;
        animation: gradient-shift 8s ease-in-out infinite;
      }
      
      .theme-card {
        background: linear-gradient(135deg, rgb(var(--card)) 0%, rgb(var(--card) / 0.95) 100%);
        backdrop-filter: blur(8px);
        border: 1px solid rgb(var(--primary) / 0.1);
        box-shadow: var(--shadow-card);
      }
      
      .theme-button {
        background: linear-gradient(135deg, rgb(var(--primary)) 0%, rgb(var(--accent)) 100%);
        color: rgb(var(--primary-foreground));
        border: none;
        box-shadow: var(--shadow-card);
        transition: all 0.3s ease;
      }
      
      .theme-button:hover {
        transform: scale(1.05);
        box-shadow: var(--shadow-glow);
      }
      
      @keyframes gradient-shift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
    `
  }

  const getColor = (colorKey: keyof ThemeColors): string => {
    if (!themeConfig) return ''
    return themeConfig.colors[currentMode][colorKey]
  }

  const getGradient = (gradientKey: keyof ThemeGradients): string => {
    if (!themeConfig) return ''
    return themeConfig.gradients[gradientKey][currentMode]
  }

  const getShadow = (shadowKey: keyof ThemeShadows): string => {
    if (!themeConfig) return ''
    return themeConfig.shadows[shadowKey][currentMode]
  }

  const getSemantic = (semanticKey: keyof ThemeSemantic): string => {
    if (!themeConfig) return ''
    return themeConfig.semantic[semanticKey][currentMode]
  }

  const setCurrentTheme = (themeName: string) => {
    setCurrentThemeState(themeName)
    loadTheme(themeName)
    localStorage.setItem('undernet-theme', themeName)
  }

  // Load theme from localStorage or default on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('undernet-theme') || 'default'
    setCurrentThemeState(savedTheme)
    loadTheme(savedTheme)
  }, [])

  // Update CSS when theme mode changes
  useEffect(() => {
    if (themeConfig) {
      console.log('Mode changed, reapplying theme:', currentMode)
      applyThemeToCSS(themeConfig, currentMode)
    }
  }, [themeConfig, currentMode])

  return (
    <ThemeSystemContext.Provider
      value={{
        theme: themeConfig,
        currentMode,
        currentTheme,
        availableThemes,
        getColor,
        getGradient,
        getShadow,
        getSemantic,
        loadTheme,
        setCurrentTheme
      }}
    >
      {children}
    </ThemeSystemContext.Provider>
  )
}